import type { ArrowDefinition, Cell, Direction } from '../game/model/types';
import { directionBetween, directionVectors } from '../game/model/cells';
import { nextInt, pickOne, popRandom, shuffle, type Rng } from './rng';
import { isSelfRayValid, tryMergePaths, type SnakePath } from './partition';
import { bothOppositeSides, sideAdjacencyMask } from './interest';
import type { ChainTrapParams, EdgeParams, WardenParams } from './types';

export interface OrientedArrows {
  readonly arrows: readonly ArrowDefinition[];
  readonly solutionOrder: readonly number[];
  readonly initialRemovableCount?: number;
  readonly longBlockerRatio?: number;
  readonly peelDepth?: number;
  /** Arrow indexes in removal order; authoring/test metadata only. */
  readonly wardenArrowIndexes?: readonly number[];
  /** Edge-sentinel arrow indexes in removal-order metadata. */
  readonly edgeSentinelArrowIndexes?: readonly number[];
  /** D6 authored/reserved interest arrow indexes in removal-order metadata. */
  readonly interestArrowIndexes?: readonly number[];
  /** Direct blocker-to-victim edges in removal-order arrow indexes. */
  readonly wardenVictimPairs?: readonly {
    readonly wardenArrowIndex: number;
    readonly victimArrowIndex: number;
  }[];
}

export interface EdgeSentinelOrientation {
  readonly pathIndex: number;
  readonly direction: Direction;
  readonly tipAtPathStart: boolean;
}

export interface OrientPathsOptions {
  readonly wardenParams?: WardenParams;
  readonly wardenPathsPrepared?: boolean;
  readonly chainTrapParams?: ChainTrapParams;
  readonly authoredInterestTailPathCount?: number;
  readonly edgeParams?: EdgeParams;
}

interface OrientationCandidate extends EdgeSentinelOrientation {
  readonly cells: readonly Cell[];
}

// キーは配列の同一性のみ。渡した path とその Cell は以後不変であることが契約
// （merge 等でパスを変える場合は必ず新しい配列を作ること。in-place 変更は古い候補を返す）
const orientationOptionsByPath = new WeakMap<
  SnakePath,
  Map<number, OrientationCandidate[]>
>();

interface MergeCandidate {
  readonly keepIndex: number;
  readonly removeIndex: number;
  readonly path: SnakePath;
}

interface InterestArrow {
  readonly arrow: Omit<ArrowDefinition, 'id'>;
  readonly pathIndex: number;
  readonly initiallyRemovable: boolean;
}

interface InitialInterestPlan {
  readonly orientations: ReadonlyMap<number, OrientationCandidate>;
}

interface InterestSelectionContext {
  readonly componentsByDirection: ReadonlyMap<Direction, readonly (readonly InterestArrow[])[]>;
  readonly currentChainRuns: number;
  readonly currentTrapCount: number;
}

function sameOrientation(
  left: EdgeSentinelOrientation,
  right: EdgeSentinelOrientation,
): boolean {
  return left.pathIndex === right.pathIndex
    && left.direction === right.direction
    && left.tipAtPathStart === right.tipAtPathStart;
}

function planInitialInterest(
  pathsByIndex: ReadonlyMap<number, SnakePath>,
  initialOccupancy: Int32Array,
  width: number,
  height: number,
  params: ChainTrapParams,
  areAdjacent: (leftPathIndex: number, rightPathIndex: number, direction: Direction) => boolean,
): InitialInterestPlan {
  const exposedByDirection = new Map<Direction, OrientationCandidate[]>();
  for (const direction of ['up', 'right', 'down', 'left'] as const) {
    exposedByDirection.set(direction, []);
  }
  for (const [pathIndex, path] of pathsByIndex) {
    for (const candidate of orientationOptionsFor(path, pathIndex)) {
      if (rayIsClear(candidate.cells, candidate.direction, initialOccupancy, pathIndex, width, height)) {
        exposedByDirection.get(candidate.direction)?.push(candidate);
      }
    }
  }

  if (params.trapCountTarget[0] === 0) {
    const orientations = new Map<number, OrientationCandidate>();
    let remainingRuns = params.chainRunTarget;
    const components = [...exposedByDirection.entries()].flatMap(([direction, candidates]) => {
      const remaining = new Set(new Map(candidates.map((candidate) =>
        [candidate.pathIndex, candidate])).values());
      const groups: OrientationCandidate[][] = [];
      while (remaining.size > 0) {
        const first = remaining.values().next().value as OrientationCandidate;
        remaining.delete(first);
        const component = [first];
        for (let cursor = 0; cursor < component.length; cursor += 1) {
          const current = component[cursor];
          if (current === undefined) continue;
          for (const candidate of [...remaining]) {
            if (areAdjacent(current.pathIndex, candidate.pathIndex, direction)) {
              remaining.delete(candidate);
              component.push(candidate);
            }
          }
        }
        if (component.length >= 4) groups.push(component);
      }
      return groups;
    }).sort((left, right) => right.length - left.length
      || left[0]!.pathIndex - right[0]!.pathIndex);
    for (const component of components) {
      component.slice(0, 4).forEach((candidate) =>
        orientations.set(candidate.pathIndex, candidate));
      remainingRuns -= 1;
      if (remainingRuns === 0) break;
    }
    return { orientations };
  }

  const orientations = new Map<number, OrientationCandidate>();
  const usedPaths = new Set<number>();
  const trapGroupCount = params.trapCountTarget[0];
  const groupCount = Math.max(params.chainRunTarget, trapGroupCount);
  let plannedTrapCount = 0;
  for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
    const requiredSize = groupIndex < params.chainRunTarget ? 4 : 3;
    const needsTrap = plannedTrapCount < trapGroupCount;
    const groupCandidates: Array<{
      readonly direction: Direction;
      readonly component: readonly OrientationCandidate[];
      readonly trapCandidates: readonly OrientationCandidate[];
    }> = [];
    for (const direction of ['up', 'right', 'down', 'left'] as const) {
      const byPath = new Map<number, OrientationCandidate>();
      for (const candidate of exposedByDirection.get(direction) ?? []) {
        if (!usedPaths.has(candidate.pathIndex) && !byPath.has(candidate.pathIndex)) {
          byPath.set(candidate.pathIndex, candidate);
        }
      }
      const remaining = new Set(byPath.values());
      while (remaining.size > 0) {
        const first = remaining.values().next().value as OrientationCandidate;
        remaining.delete(first);
        const component = [first];
        for (let cursor = 0; cursor < component.length; cursor += 1) {
          const current = component[cursor];
          if (current === undefined) continue;
          for (const candidate of [...remaining]) {
            if (areAdjacent(current.pathIndex, candidate.pathIndex, direction)) {
              remaining.delete(candidate);
              component.push(candidate);
            }
          }
        }
        if (component.length < requiredSize) continue;
        const opposite = ({ up: 'down', right: 'left', down: 'up', left: 'right' } as const)[
          direction
        ];
        const trapCandidates = [...pathsByIndex].flatMap(([pathIndex, path]) => {
          if (usedPaths.has(pathIndex) || component.some((item) => item.pathIndex === pathIndex)) {
            return [];
          }
          return orientationOptionsFor(path, pathIndex).filter((candidate) =>
            candidate.direction === opposite
            && !rayIsClear(
              candidate.cells,
              candidate.direction,
              initialOccupancy,
              pathIndex,
              width,
              height,
            )
            && component.some((item) =>
              areAdjacent(item.pathIndex, pathIndex, direction)));
        });
        if (!needsTrap || trapCandidates.length > 0) {
          groupCandidates.push({ direction, component, trapCandidates });
        }
      }
    }
    groupCandidates.sort((left, right) =>
      Number(right.trapCandidates.length > 0) - Number(left.trapCandidates.length > 0)
      || right.component.length - left.component.length
      || left.component[0]!.pathIndex - right.component[0]!.pathIndex);
    const selected = groupCandidates[0];
    if (selected === undefined) break;
    const group = selected.component.slice(0, Math.min(8, requiredSize));
    for (const candidate of group) {
      orientations.set(candidate.pathIndex, candidate);
      usedPaths.add(candidate.pathIndex);
    }
    if (needsTrap) {
      const usedLocalMembers = new Set<number>();
      for (const trap of [...selected.trapCandidates].sort((left, right) =>
        left.pathIndex - right.pathIndex)) {
        const localMembers = group.filter((member) =>
          areAdjacent(member.pathIndex, trap.pathIndex, selected.direction));
        if (localMembers.length === 0
          || localMembers.some((member) => usedLocalMembers.has(member.pathIndex))) {
          continue;
        }
        orientations.set(trap.pathIndex, trap);
        usedPaths.add(trap.pathIndex);
        localMembers.forEach((member) => usedLocalMembers.add(member.pathIndex));
        plannedTrapCount += 1;
        if (plannedTrapCount >= trapGroupCount) break;
      }
    }
  }
  return { orientations };
}

function interestComponents(
  arrows: readonly InterestArrow[],
  direction: Direction,
  areAdjacent: (leftPathIndex: number, rightPathIndex: number, direction: Direction) => boolean,
): readonly (readonly InterestArrow[])[] {
  const remaining = new Set(arrows.filter((arrow) =>
    arrow.initiallyRemovable && arrow.arrow.direction === direction));
  const components: InterestArrow[][] = [];
  while (remaining.size > 0) {
    const first = remaining.values().next().value as InterestArrow;
    remaining.delete(first);
    const component = [first];
    for (let cursor = 0; cursor < component.length; cursor += 1) {
      const current = component[cursor];
      if (current === undefined) continue;
      for (const candidate of [...remaining]) {
        if (areAdjacent(current.pathIndex, candidate.pathIndex, direction)) {
          remaining.delete(candidate);
          component.push(candidate);
        }
      }
    }
    components.push(component);
  }
  return components;
}

function partialTrapCount(
  arrows: readonly InterestArrow[],
  areAdjacent: (leftPathIndex: number, rightPathIndex: number, direction: Direction) => boolean,
  componentsByDirection?: ReadonlyMap<Direction, readonly (readonly InterestArrow[])[]>,
): number {
  const traps = new Set<number>();
  for (const direction of ['up', 'right', 'down', 'left'] as const) {
    const components = (componentsByDirection?.get(direction)
      ?? interestComponents(arrows, direction, areAdjacent))
      .filter((component) => component.length >= 3);
    for (const component of components) {
      const reversed = arrows.filter((arrow) =>
        !arrow.initiallyRemovable
        && arrow.arrow.direction === ({ up: 'down', right: 'left', down: 'up', left: 'right' } as const)[direction]
        && component.some((member) =>
          areAdjacent(member.pathIndex, arrow.pathIndex, direction)));
      if (reversed.length === 1) traps.add(reversed[0]!.pathIndex);
    }
  }
  return traps.size;
}

function interestSelectionContext(
  selected: readonly InterestArrow[],
  areAdjacent: (leftPathIndex: number, rightPathIndex: number, direction: Direction) => boolean,
): InterestSelectionContext {
  const componentsByDirection = new Map<
    Direction,
    readonly (readonly InterestArrow[])[]
  >();
  let currentChainRuns = 0;
  for (const direction of ['up', 'right', 'down', 'left'] as const) {
    const components = interestComponents(selected, direction, areAdjacent);
    componentsByDirection.set(direction, components);
    currentChainRuns += components.filter((component) => component.length >= 4).length;
  }
  return {
    componentsByDirection,
    currentChainRuns,
    currentTrapCount: partialTrapCount(selected, areAdjacent, componentsByDirection),
  };
}

export function trapOpportunityScore(
  currentTrapCount: number,
  params: ChainTrapParams,
  adjacentSideMask: number,
): number {
  if (adjacentSideMask === 0) return 0;
  const embedded = adjacentSideMask === bothOppositeSides;
  if (currentTrapCount < params.trapCountTarget[0]) return embedded ? 1800 : 1400;
  if (currentTrapCount < params.trapCountTarget[1]) return embedded ? 500 : 300;
  return currentTrapCount >= params.maximumTrapCount ? -2000 : -200;
}

function interestSelectionScore(
  candidate: OrientationCandidate,
  initiallyRemovable: boolean,
  selected: readonly InterestArrow[],
  params: ChainTrapParams,
  totalArrowCount: number,
  areAdjacent: (leftPathIndex: number, rightPathIndex: number, direction: Direction) => boolean,
  context: InterestSelectionContext,
): number {
  const { componentsByDirection, currentChainRuns, currentTrapCount } = context;
  if (initiallyRemovable) {
    const projectedRate = (selected.filter((arrow) => arrow.initiallyRemovable).length + 1)
      / totalArrowCount;
    const adjacentComponents = (componentsByDirection.get(candidate.direction) ?? [])
      .filter((component) => component.some((arrow) =>
        areAdjacent(arrow.pathIndex, candidate.pathIndex, candidate.direction)));
    const joinedSize = 1 + adjacentComponents.reduce(
      (sum, component) => sum + component.length,
      0,
    );
    const previousRunCount = adjacentComponents.filter((component) =>
      component.length >= 4).length;
    const runDelta = Number(joinedSize >= 4) - previousRunCount;
    let score = Math.min(joinedSize, 4) * 100 + runDelta * 1000;
    if (currentChainRuns < params.chainRunTarget) {
      score += runDelta > 0 ? 2000 : Math.min(joinedSize, 4) * 100;
    }
    if (projectedRate > params.initialRemovableRate[1]) score -= 2000;
    if (projectedRate < params.initialRemovableRate[0]) score += 20;
    return score;
  }

  const opposite = ({ up: 'down', right: 'left', down: 'up', left: 'right' } as const)[
    candidate.direction
  ];
  const adjacencyMasks = (componentsByDirection.get(opposite) ?? [])
    .filter((component) => component.length >= 3)
    .map((component) => component.reduce((mask, arrow) =>
      mask | sideAdjacencyMask(arrow.arrow.cells, candidate.cells, opposite), 0));
  const adjacentSideMask = adjacencyMasks.includes(bothOppositeSides)
    ? bothOppositeSides
    : Number(adjacencyMasks.some((mask) => mask !== 0));
  return trapOpportunityScore(currentTrapCount, params, adjacentSideMask);
}

export function rayIsClear(
  cells: readonly Cell[],
  direction: Direction,
  occupancy: Int32Array,
  ownIndex: number,
  width: number,
  height: number,
): boolean {
  const tip = cells.at(-1);
  if (tip === undefined) {
    return false;
  }
  const step = directionVectors[direction];
  for (
    let x = tip.x + step.x, y = tip.y + step.y;
    x >= 0 && x < width && y >= 0 && y < height;
    x += step.x, y += step.y
  ) {
    const occupant = occupancy[y * width + x] ?? 0;
    if (occupant !== 0 && occupant !== ownIndex + 1) {
      return false;
    }
  }
  return true;
}

function orientationOptionsFor(
  path: SnakePath,
  pathIndex: number,
): OrientationCandidate[] {
  const cachedByIndex = orientationOptionsByPath.get(path);
  const cached = cachedByIndex?.get(pathIndex);
  if (cached !== undefined) {
    return cached;
  }
  const candidates: OrientationCandidate[] = [];
  for (const reverse of [false, true]) {
    if (!isSelfRayValid(path, reverse)) {
      continue;
    }
    const cells = reverse ? [...path].reverse() : [...path];
    const tip = cells.at(-1);
    const beforeTip = cells.at(-2);
    if (tip === undefined || beforeTip === undefined) {
      continue;
    }
    const direction = directionBetween(beforeTip, tip);
    candidates.push({ pathIndex, cells, direction, tipAtPathStart: reverse });
  }
  const optionsByIndex = cachedByIndex ?? new Map<number, OrientationCandidate[]>();
  optionsByIndex.set(pathIndex, candidates);
  if (cachedByIndex === undefined) orientationOptionsByPath.set(path, optionsByIndex);
  return candidates;
}

export function selectEdgeSentinels(
  pathsByIndex: ReadonlyMap<number, SnakePath>,
  boardWidth: number,
  boardHeight: number,
  rng: Rng,
  params: EdgeParams,
  excludedPathIndexes: ReadonlySet<number>,
): Map<number, EdgeSentinelOrientation> {
  const horizontalBand = Math.max(2, Math.floor(boardWidth / params.exitBandDivisor));
  const verticalBand = Math.max(2, Math.floor(boardHeight / params.exitBandDivisor));
  const horizontalSides = [
    {
      inwardDirection: 'right' as const,
      contains: ({ x }: { readonly x: number }) => x < horizontalBand,
    },
    {
      inwardDirection: 'left' as const,
      contains: ({ x }: { readonly x: number }) => x >= boardWidth - horizontalBand,
    },
  ];
  const verticalSides = [
    {
      inwardDirection: 'down' as const,
      contains: ({ y }: { readonly y: number }) => y < verticalBand,
    },
    {
      inwardDirection: 'up' as const,
      contains: ({ y }: { readonly y: number }) => y >= boardHeight - verticalBand,
    },
  ];
  const requestedCount = nextInt(
    rng,
    params.sentinelCount[0],
    params.sentinelCount[1],
  );
  const centersByPathIndex = new Map([...pathsByIndex].map(([pathIndex, path]) =>
    [pathIndex, pathCenter(path)]));
  const sides = [
    ...shuffle(rng, horizontalSides),
    ...shuffle(rng, verticalSides),
  ].map(({ inwardDirection, contains }) => ({
    inwardDirection,
    eligible: [...pathsByIndex].flatMap(([pathIndex, path]) => {
      if (excludedPathIndexes.has(pathIndex)) return [];
      const center = centersByPathIndex.get(pathIndex);
      if (center === undefined || !contains(center)) return [];
      const orientation = orientationOptionsFor(path, pathIndex)
        .find((candidate) => candidate.direction === inwardDirection);
      return orientation === undefined ? [] : [orientation];
    }),
  }));
  const sentinels = new Map<number, EdgeSentinelOrientation>();
  while (sentinels.size < requestedCount) {
    let selectedInRound = false;
    for (const side of sides) {
      const selected = popRandom(rng, side.eligible);
      if (selected === undefined) continue;
      sentinels.set(selected.pathIndex, selected);
      for (const otherSide of sides) {
        const duplicateIndex = otherSide.eligible.findIndex((candidate) =>
          candidate.pathIndex === selected.pathIndex);
        if (duplicateIndex >= 0) otherSide.eligible.splice(duplicateIndex, 1);
      }
      selectedInRound = true;
      if (sentinels.size === requestedCount) break;
    }
    if (!selectedInRound) break;
  }
  return sentinels;
}

function orientationsFor(
  path: SnakePath,
  pathIndex: number,
  occupancy: Int32Array,
  width: number,
  height: number,
): OrientationCandidate[] {
  return orientationOptionsFor(path, pathIndex).filter((candidate) => rayIsClear(
    candidate.cells,
    candidate.direction,
    occupancy,
    pathIndex,
    width,
    height,
  ));
}

function mergeCandidates(
  remaining: ReadonlySet<number>,
  pathsByIndex: ReadonlyMap<number, SnakePath>,
  maximumLength: number,
): MergeCandidate[] {
  const indices = [...remaining];
  const candidates: MergeCandidate[] = [];
  for (let leftPosition = 0; leftPosition < indices.length; leftPosition += 1) {
    const leftIndex = indices[leftPosition];
    const leftPath = leftIndex === undefined ? undefined : pathsByIndex.get(leftIndex);
    if (leftIndex === undefined || leftPath === undefined) continue;
    for (let rightPosition = leftPosition + 1; rightPosition < indices.length; rightPosition += 1) {
      const rightIndex = indices[rightPosition];
      const rightPath = rightIndex === undefined ? undefined : pathsByIndex.get(rightIndex);
      if (rightIndex === undefined || rightPath === undefined) continue;
      const merged = tryMergePaths(leftPath, rightPath, maximumLength);
      if (merged !== null) {
        candidates.push({ keepIndex: leftIndex, removeIndex: rightIndex, path: merged });
      }
    }
  }
  return candidates;
}

export function pathCenter(path: SnakePath): { readonly x: number; readonly y: number } {
  return {
    x: path.reduce((sum, cell) => sum + cell.x, 0) / path.length,
    y: path.reduce((sum, cell) => sum + cell.y, 0) / path.length,
  };
}

function blockerCoverageByPathIndex(
  pathsByIndex: ReadonlyMap<number, SnakePath>,
  occupancy: Int32Array,
  boardWidth: number,
  boardHeight: number,
): ReadonlyMap<number, number> {
  const blockerCoverage = new Map<number, number>();
  for (const [pathIndex, path] of pathsByIndex) {
    for (const option of orientationOptionsFor(path, pathIndex)) {
      const blocker = firstBlockerPathIndex(option, occupancy, boardWidth, boardHeight);
      if (blocker !== undefined) {
        blockerCoverage.set(blocker, (blockerCoverage.get(blocker) ?? 0) + 1);
      }
    }
  }
  return blockerCoverage;
}

function selectWardenPathIndexes(
  pathsByIndex: ReadonlyMap<number, SnakePath>,
  boardWidth: number,
  boardHeight: number,
  blockerCoverage: ReadonlyMap<number, number>,
  params: WardenParams,
  rng: Rng,
): Set<number> {
  const requestedCount = nextInt(rng, params.count[0], params.count[1]);
  const eligible = [...pathsByIndex]
    .map(([pathIndex, path]) => ({
      path,
      pathIndex,
      center: pathCenter(path),
      coverage: blockerCoverage.get(pathIndex) ?? 0,
    }))
    .filter(({ path }) => path.length >= params.minimumLength)
    .sort((left, right) => right.coverage - left.coverage
      || right.path.length - left.path.length
      || left.pathIndex - right.pathIndex);
  const pool = eligible.slice(0, Math.max(
    requestedCount,
    requestedCount * params.candidatePoolMultiplier,
  ));
  if (pool.length <= requestedCount) return new Set(pool.map(({ pathIndex }) => pathIndex));

  const selected = [pickOne(rng, pool.slice(0, Math.min(pool.length, 2)))];
  const maximumCoverage = Math.max(1, ...pool.map(({ coverage }) => coverage));
  const diagonal = Math.hypot(boardWidth, boardHeight);
  while (selected.length < requestedCount) {
    const remaining = pool.filter(({ pathIndex }) =>
      !selected.some((candidate) => candidate.pathIndex === pathIndex));
    if (remaining.length === 0) break;
    const scored = remaining.map((candidate) => {
      const minimumDistance = Math.min(...selected.map((warden) => Math.hypot(
        candidate.center.x - warden.center.x,
        candidate.center.y - warden.center.y,
      )));
      return {
        candidate,
        score: candidate.coverage / maximumCoverage + minimumDistance / diagonal,
      };
    });
    const maximumScore = Math.max(...scored.map(({ score }) => score));
    selected.push(pickOne(
      rng,
      scored.filter(({ score }) => score === maximumScore)
        .map(({ candidate }) => candidate),
    ));
  }
  return new Set(selected.map(({ pathIndex }) => pathIndex));
}

function firstBlockerPathIndex(
  candidate: OrientationCandidate,
  occupancy: Int32Array,
  width: number,
  height: number,
): number | undefined {
  const tip = candidate.cells.at(-1);
  if (tip === undefined) return undefined;
  const step = directionVectors[candidate.direction];
  for (
    let x = tip.x + step.x, y = tip.y + step.y;
    x >= 0 && x < width && y >= 0 && y < height;
    x += step.x, y += step.y
  ) {
    const occupant = occupancy[y * width + x] ?? 0;
    if (occupant !== 0 && occupant !== candidate.pathIndex + 1) return occupant - 1;
  }
  return undefined;
}

function prefers(rng: Rng, probability: number): boolean {
  return probability === 1 || rng() < probability;
}

function exposedPathIndexes(
  pathsByIndex: ReadonlyMap<number, SnakePath>,
  occupancy: Int32Array,
  width: number,
  height: number,
): Set<number> {
  return new Set([...pathsByIndex].flatMap(([pathIndex, path]) =>
    orientationsFor(path, pathIndex, occupancy, width, height).length > 0
      ? [pathIndex]
      : []));
}

function hasGreedyPeelOrder(
  sourcePaths: ReadonlyMap<number, SnakePath>,
  sourceOccupancy: Int32Array,
  width: number,
  height: number,
): boolean {
  const remaining = new Set(sourcePaths.keys());
  const occupancy = sourceOccupancy.slice();
  while (remaining.size > 0) {
    const candidate = [...remaining].flatMap((pathIndex) => {
      const path = sourcePaths.get(pathIndex);
      return path === undefined ? [] : orientationsFor(path, pathIndex, occupancy, width, height);
    })[0];
    if (candidate === undefined) return false;
    remaining.delete(candidate.pathIndex);
    candidate.cells.forEach(({ x, y }) => {
      occupancy[y * width + x] = 0;
    });
  }
  return true;
}

function reduceInitialExposure(
  pathsByIndex: Map<number, SnakePath>,
  occupancy: Int32Array,
  width: number,
  height: number,
  maximumLength: number,
  targetRate: number,
  rng: Rng,
  protectedPathIndexes: ReadonlySet<number> = new Set(),
): void {
  for (let mergeCount = 0; mergeCount < 64; mergeCount += 1) {
    const exposed = exposedPathIndexes(pathsByIndex, occupancy, width, height);
    if (exposed.size / pathsByIndex.size <= targetRate) return;
    const candidates = mergeCandidates(
      new Set(pathsByIndex.keys()), pathsByIndex, maximumLength,
    ).filter((candidate) => !protectedPathIndexes.has(candidate.keepIndex)
      && !protectedPathIndexes.has(candidate.removeIndex)).flatMap((candidate) => {
      const exposedInputs = Number(exposed.has(candidate.keepIndex))
        + Number(exposed.has(candidate.removeIndex));
      if (exposedInputs === 0) return [];
      const candidateOccupancy = occupancy.slice();
      candidate.path.forEach(({ x, y }) => {
        candidateOccupancy[y * width + x] = candidate.keepIndex + 1;
      });
      const mergedIsExposed = orientationsFor(
        candidate.path, candidate.keepIndex, candidateOccupancy, width, height,
      ).length > 0;
      const reduction = exposedInputs - Number(mergedIsExposed);
      return reduction > 0 ? [{ ...candidate, candidateOccupancy, reduction }] : [];
    });
    const maximumReduction = Math.max(0, ...candidates.map(({ reduction }) => reduction));
    let selected: typeof candidates[number] | undefined;
    for (const candidate of shuffle(
      rng, candidates.filter(({ reduction }) => reduction === maximumReduction),
    )) {
      const candidatePaths = new Map(pathsByIndex);
      candidatePaths.set(candidate.keepIndex, candidate.path);
      candidatePaths.delete(candidate.removeIndex);
      if (hasGreedyPeelOrder(candidatePaths, candidate.candidateOccupancy, width, height)) {
        selected = candidate;
        break;
      }
    }
    if (selected === undefined) return;
    pathsByIndex.set(selected.keepIndex, selected.path);
    pathsByIndex.delete(selected.removeIndex);
    occupancy.set(selected.candidateOccupancy);
  }
}

export function prepareWardenPaths(
  paths: readonly SnakePath[],
  boardWidth: number,
  boardHeight: number,
  rng: Rng,
  params: WardenParams,
  protectedTailPathCount = 0,
): readonly SnakePath[] {
  const pathsByIndex = new Map(paths.map((path, index) => [index, path]));
  const occupancy = new Int32Array(boardWidth * boardHeight);
  paths.forEach((path, pathIndex) => path.forEach(({ x, y }) => {
    occupancy[y * boardWidth + x] = pathIndex + 1;
  }));
  const firstProtectedPathIndex = Math.max(0, paths.length - protectedTailPathCount);
  const protectedPathIndexes = new Set(Array.from(
    { length: Math.min(paths.length, protectedTailPathCount) },
    (_, index) => firstProtectedPathIndex + index,
  ));
  const mergeLengthLimit = Math.ceil(
    params.mergeLengthMultiplier * Math.max(0, ...paths.map((path) => path.length)),
  );
  reduceInitialExposure(
    pathsByIndex,
    occupancy,
    boardWidth,
    boardHeight,
    mergeLengthLimit,
    params.initialRemovableRateTarget,
    rng,
    protectedPathIndexes,
  );
  return [...pathsByIndex.values()];
}

function firstArrowBlockerIndex(
  arrows: readonly ArrowDefinition[],
  arrowIndex: number,
  occupancy: Int32Array,
  width: number,
  height: number,
): number | undefined {
  const arrow = arrows[arrowIndex];
  const tip = arrow?.cells.at(-1);
  if (arrow === undefined || tip === undefined) return undefined;
  const step = directionVectors[arrow.direction];
  for (
    let x = tip.x + step.x, y = tip.y + step.y;
    x >= 0 && x < width && y >= 0 && y < height;
    x += step.x, y += step.y
  ) {
    const blocker = (occupancy[y * width + x] ?? 0) - 1;
    if (blocker >= 0 && blocker !== arrowIndex) return blocker;
  }
  return undefined;
}

function splitVictimArrow(
  arrow: ArrowDefinition,
  params: WardenParams,
  solutionOccupancy: Int32Array,
  originalArrowIndex: number,
  width: number,
  height: number,
): readonly Omit<ArrowDefinition, 'id'>[] {
  type Piece = Omit<ArrowDefinition, 'id'>;
  const search = (
    remaining: readonly Cell[],
    direction: Direction,
    occupancy: Int32Array,
    pieces: readonly Piece[],
  ): readonly Piece[] | null => {
    if (pieces.length + 1 >= params.maximumVictimChunks
      || remaining.length < params.victimChunkLength + 2) {
      return rayIsClear(
        remaining,
        direction,
        occupancy,
        originalArrowIndex,
        width,
        height,
      ) ? [...pieces, { cells: remaining, direction }] : null;
    }
    for (
      let suffixLength = params.victimChunkLength;
      suffixLength <= remaining.length - 2;
      suffixLength += 1
    ) {
      const candidateIndex = remaining.length - suffixLength;
      const before = remaining[candidateIndex - 2];
      const boundary = remaining[candidateIndex - 1];
      const after = remaining[candidateIndex];
      if (before === undefined || boundary === undefined || after === undefined
        || directionBetween(before, boundary) !== directionBetween(boundary, after)) {
        continue;
      }
      const prefix = remaining.slice(0, candidateIndex);
      const suffix = remaining.slice(candidateIndex);
      if (!isSelfRayValid(prefix, false) || !isSelfRayValid(suffix, false)) continue;
      const candidateOccupancy = occupancy.slice();
      prefix.forEach(({ x, y }) => {
        candidateOccupancy[y * width + x] = -1;
      });
      suffix.forEach(({ x, y }) => {
        candidateOccupancy[y * width + x] = originalArrowIndex + 1;
      });
      if (!rayIsClear(
        suffix,
        direction,
        candidateOccupancy,
        originalArrowIndex,
        width,
        height,
      )) continue;
      suffix.forEach(({ x, y }) => {
        candidateOccupancy[y * width + x] = 0;
      });
      prefix.forEach(({ x, y }) => {
        candidateOccupancy[y * width + x] = originalArrowIndex + 1;
      });
      const prefixDirection = directionBetween(prefix.at(-2)!, prefix.at(-1)!);
      const result = search(
        prefix,
        prefixDirection,
        candidateOccupancy,
        [...pieces, { cells: suffix, direction }],
      );
      if (result !== null) return result;
    }
    return rayIsClear(
      remaining,
      direction,
      occupancy,
      originalArrowIndex,
      width,
      height,
    ) ? [...pieces, { cells: remaining, direction }] : null;
  };
  return search(
    arrow.cells,
    arrow.direction,
    solutionOccupancy,
    [],
  ) ?? [arrow];
}

function simultaneousPeelDepth(
  arrows: readonly ArrowDefinition[],
  width: number,
  height: number,
): number {
  const occupancy = new Int32Array(width * height);
  arrows.forEach((arrow, arrowIndex) => arrow.cells.forEach(({ x, y }) => {
    occupancy[y * width + x] = arrowIndex + 1;
  }));
  const remaining = new Set(arrows.map((_, arrowIndex) => arrowIndex));
  let depth = 0;
  while (remaining.size > 0) {
    const removable = [...remaining].filter((arrowIndex) => {
      const arrow = arrows[arrowIndex];
      return arrow !== undefined && rayIsClear(
        arrow.cells,
        arrow.direction,
        occupancy,
        arrowIndex,
        width,
        height,
      );
    });
    if (removable.length === 0) return Number.POSITIVE_INFINITY;
    removable.forEach((arrowIndex) => {
      remaining.delete(arrowIndex);
      arrows[arrowIndex]?.cells.forEach(({ x, y }) => {
        occupancy[y * width + x] = 0;
      });
    });
    depth += 1;
  }
  return depth;
}

function annotateWardenDependencies(
  arrows: readonly ArrowDefinition[],
  wardenArrowIndexes: readonly number[],
  params: WardenParams,
  width: number,
  height: number,
): OrientedArrows {
  const initialOccupancy = new Int32Array(width * height);
  arrows.forEach((arrow, arrowIndex) => arrow.cells.forEach(({ x, y }) => {
    initialOccupancy[y * width + x] = arrowIndex + 1;
  }));
  const wardenSet = new Set(wardenArrowIndexes);
  const wardenVictimPairs = arrows.flatMap((_, victimArrowIndex) => {
    const wardenArrowIndex = firstArrowBlockerIndex(
      arrows,
      victimArrowIndex,
      initialOccupancy,
      width,
      height,
    );
    return wardenArrowIndex !== undefined && wardenSet.has(wardenArrowIndex)
      ? [{ wardenArrowIndex, victimArrowIndex }]
      : [];
  });
  const blockerIndexes = arrows.map((_, arrowIndex) => firstArrowBlockerIndex(
    arrows,
    arrowIndex,
    initialOccupancy,
    width,
    height,
  ));
  const blocked = blockerIndexes.filter((blocker): blocker is number => blocker !== undefined);
  const longBlocked = blocked.filter((blocker) =>
    (arrows[blocker]?.cells.length ?? 0) >= params.minimumLength);
  const initialRemovableCount = arrows.length - blocked.length;
  return {
    arrows,
    solutionOrder: arrows.map((_, index) => index),
    initialRemovableCount,
    longBlockerRatio: blocked.length === 0 ? 0 : longBlocked.length / blocked.length,
    peelDepth: simultaneousPeelDepth(arrows, width, height),
    wardenArrowIndexes,
    wardenVictimPairs,
  };
}

function splitNonWardenLongPaths(
  arrows: readonly ArrowDefinition[],
  wardenArrowIndexes: readonly number[],
  edgeSentinelArrowIndexes: readonly number[],
  interestArrowIndexes: readonly number[],
  params: WardenParams,
  width: number,
  height: number,
  rng: Rng,
): OrientedArrows {
  const initialOccupancy = new Int32Array(width * height);
  arrows.forEach((arrow, arrowIndex) => arrow.cells.forEach(({ x, y }) => {
    initialOccupancy[y * width + x] = arrowIndex + 1;
  }));
  const solutionOccupancy = initialOccupancy.slice();
  const wardenSet = new Set(wardenArrowIndexes);
  const edgeSentinelSet = new Set(edgeSentinelArrowIndexes);
  const interestSet = new Set(interestArrowIndexes);
  const expanded: ArrowDefinition[] = [];
  const expandedWardenIndexes: number[] = [];
  const expandedEdgeSentinelIndexes: number[] = [];
  const expandedInterestIndexes: number[] = [];
  arrows.forEach((arrow, arrowIndex) => {
    const blocker = firstArrowBlockerIndex(
      arrows,
      arrowIndex,
      initialOccupancy,
      width,
      height,
    );
    const blockedByLongArrow = blocker !== undefined
      && (arrows[blocker]?.cells.length ?? 0) >= params.minimumLength;
    const pieces = !wardenSet.has(arrowIndex) && blockedByLongArrow
      && prefers(rng, params.victimSplitPreference)
      ? splitVictimArrow(
        arrow,
        params,
        solutionOccupancy,
        arrowIndex,
        width,
        height,
      )
      : [arrow];
    arrow.cells.forEach(({ x, y }) => {
      solutionOccupancy[y * width + x] = 0;
    });
    if (wardenSet.has(arrowIndex)) expandedWardenIndexes.push(expanded.length);
    pieces.forEach((piece) => {
      if (edgeSentinelSet.has(arrowIndex)) expandedEdgeSentinelIndexes.push(expanded.length);
      if (interestSet.has(arrowIndex)) expandedInterestIndexes.push(expanded.length);
      expanded.push({ ...piece, id: `path-${expanded.length}` });
    });
  });
  return {
    ...annotateWardenDependencies(
      expanded,
      expandedWardenIndexes,
      params,
      width,
      height,
    ),
    edgeSentinelArrowIndexes: expandedEdgeSentinelIndexes,
    interestArrowIndexes: expandedInterestIndexes,
  };
}

export function orientPaths(
  paths: readonly SnakePath[],
  boardWidth: number,
  boardHeight: number,
  rng: Rng,
  initiallyBlockedPreference: number | boolean = 0,
  options: OrientPathsOptions = {},
): OrientedArrows | null {
  const {
    wardenParams,
    wardenPathsPrepared = false,
    chainTrapParams,
    authoredInterestTailPathCount = 0,
    edgeParams,
  } = options;
  const blockedPreference = typeof initiallyBlockedPreference === 'boolean'
    ? Number(initiallyBlockedPreference)
    : initiallyBlockedPreference;
  const pathsByIndex = new Map(paths.map((path, index) => [index, path]));
  const firstAuthoredPathIndex = Math.max(0, paths.length - authoredInterestTailPathCount);
  const authoredInterestPathIndexes = new Set(Array.from(
    { length: Math.min(paths.length, authoredInterestTailPathCount) },
    (_, index) => firstAuthoredPathIndex + index,
  ));
  const remaining = new Set(pathsByIndex.keys());
  const occupancy = new Int32Array(boardWidth * boardHeight);
  paths.forEach((path, pathIndex) => path.forEach((cell) => {
    occupancy[cell.y * boardWidth + cell.x] = pathIndex + 1;
  }));
  const mergeLengthLimit = Math.ceil(
    (wardenParams?.mergeLengthMultiplier ?? 1.5)
    * Math.max(0, ...paths.map((path) => path.length)),
  );
  if (wardenParams !== undefined && !wardenPathsPrepared) {
    reduceInitialExposure(
      pathsByIndex,
      occupancy,
      boardWidth,
      boardHeight,
      mergeLengthLimit,
      wardenParams.initialRemovableRateTarget,
      rng,
    );
    remaining.clear();
    pathsByIndex.forEach((_, pathIndex) => remaining.add(pathIndex));
  }
  const initialOccupancy = blockedPreference > 0
    || wardenParams !== undefined
    || chainTrapParams !== undefined
    ? occupancy.slice()
    : undefined;
  const adjacencyCacheSideLength = paths.length;
  const adjacencyCache = new Uint8Array(
    adjacencyCacheSideLength * adjacencyCacheSideLength * 2,
  );
  const arePathsSideAdjacent = (
    leftPathIndex: number,
    rightPathIndex: number,
    direction: Direction,
  ): boolean => {
    const horizontal = direction === 'left' || direction === 'right';
    const low = Math.min(leftPathIndex, rightPathIndex);
    const high = Math.max(leftPathIndex, rightPathIndex);
    const key = (low * adjacencyCacheSideLength + high) * 2 + Number(horizontal);
    const cached = adjacencyCache[key];
    if (cached !== 0) return cached === 2;
    const left = pathsByIndex.get(leftPathIndex);
    const right = pathsByIndex.get(rightPathIndex);
    if (left === undefined || right === undefined) return false;
    const rightCells = new Set(right.map(({ x, y }) => `${x},${y}`));
    const adjacent = left.some(({ x, y }) => horizontal
      ? rightCells.has(`${x},${y - 1}`) || rightCells.has(`${x},${y + 1}`)
      : rightCells.has(`${x - 1},${y}`) || rightCells.has(`${x + 1},${y}`));
    adjacencyCache[key] = adjacent ? 2 : 1;
    return adjacent;
  };
  const blockerCoverage = wardenParams === undefined
    ? new Map<number, number>()
    : blockerCoverageByPathIndex(pathsByIndex, occupancy, boardWidth, boardHeight);
  const initialInterestPlan = chainTrapParams === undefined || initialOccupancy === undefined
    ? undefined
    : planInitialInterest(
      pathsByIndex,
      initialOccupancy,
      boardWidth,
      boardHeight,
      chainTrapParams,
      arePathsSideAdjacent,
    );
  const wardenPathIndexes = wardenParams === undefined
    ? new Set<number>()
    : selectWardenPathIndexes(
      pathsByIndex,
      boardWidth,
      boardHeight,
      blockerCoverage,
      wardenParams,
      rng,
    );
  const interestPathIndexes = new Set([
    ...authoredInterestPathIndexes,
    ...(initialInterestPlan?.orientations.keys() ?? []),
  ]);
  const edgeSentinels = edgeParams === undefined
    ? new Map<number, EdgeSentinelOrientation>()
    : selectEdgeSentinels(
      pathsByIndex,
      boardWidth,
      boardHeight,
      rng,
      edgeParams,
      new Set([
        ...wardenPathIndexes,
        ...interestPathIndexes,
      ]),
    );
  const removedWardenPathIndexes = new Set<number>();
  const longPathIndexes = new Set([...pathsByIndex]
    .filter(([, path]) => path.length >= (wardenParams?.minimumLength ?? Number.POSITIVE_INFINITY))
    .map(([pathIndex]) => pathIndex));
  const removedLongPathIndexes = new Set<number>();
  const arrowIndexByPathIndex = new Map<number, number>();
  const wardenArrowIndexes: number[] = [];
  const edgeSentinelArrowIndexes: number[] = [];
  const interestArrowIndexes: number[] = [];
  const wardenVictimPairs: Array<{
    readonly wardenArrowIndex: number;
    readonly victimArrowIndex: number;
  }> = [];
  const arrows: ArrowDefinition[] = [];
  const interestArrows: InterestArrow[] = [];
  const orientationOptionsCache = new Map<
    number,
    { readonly path: SnakePath; readonly options: readonly OrientationCandidate[] }
  >();
  const cachedOrientationsFor = (
    path: SnakePath,
    pathIndex: number,
  ): OrientationCandidate[] => {
    const cached = orientationOptionsCache.get(pathIndex);
    const options = cached?.path === path
      ? cached.options
      : orientationOptionsFor(path, pathIndex);
    if (cached?.path !== path) {
      orientationOptionsCache.set(pathIndex, { path, options });
    }
    const sentinel = edgeSentinels.get(pathIndex);
    return options.filter((candidate) =>
      (sentinel === undefined || sameOrientation(candidate, sentinel))
      && rayIsClear(
        candidate.cells,
        candidate.direction,
        occupancy,
        pathIndex,
        boardWidth,
        boardHeight,
      ));
  };
  let repairCount = 0;
  let initialRemovableCount = 0;

  while (remaining.size > 0) {
    const candidates = [...remaining].flatMap((pathIndex) => {
      const path = pathsByIndex.get(pathIndex);
      return path === undefined
        ? []
        : cachedOrientationsFor(path, pathIndex);
    });
    if (candidates.length === 0) {
      if (edgeSentinels.size > 0) {
        edgeSentinels.delete(pickOne(rng, [...edgeSentinels.keys()]));
        continue;
      }
      if (repairCount >= 16) return null;
      const merge = shuffle(rng, mergeCandidates(remaining, pathsByIndex, mergeLengthLimit)
        .filter((candidate) => !authoredInterestPathIndexes.has(candidate.keepIndex)
          && !authoredInterestPathIndexes.has(candidate.removeIndex)))[0];
      if (merge === undefined) return null;
      pathsByIndex.set(merge.keepIndex, merge.path);
      pathsByIndex.delete(merge.removeIndex);
      remaining.delete(merge.removeIndex);
      adjacencyCache.fill(0);
      if (wardenPathIndexes.delete(merge.removeIndex)) wardenPathIndexes.add(merge.keepIndex);
      if (interestPathIndexes.delete(merge.removeIndex)) interestPathIndexes.add(merge.keepIndex);
      if (longPathIndexes.delete(merge.removeIndex)) longPathIndexes.add(merge.keepIndex);
      if (merge.path.length >= (wardenParams?.minimumLength ?? Number.POSITIVE_INFINITY)) {
        longPathIndexes.add(merge.keepIndex);
      }
      merge.path.forEach((cell) => {
        occupancy[cell.y * boardWidth + cell.x] = merge.keepIndex + 1;
        if (initialOccupancy !== undefined) {
          initialOccupancy[cell.y * boardWidth + cell.x] = merge.keepIndex + 1;
        }
      });
      repairCount += 1;
      continue;
    }

    // Removing cells cannot add a blocker to another exit ray. Therefore any
    // removable choice preserves every later choice in a successful peel.
    const initiallyBlocked = initialOccupancy === undefined
      ? []
      : candidates.filter((candidate) => !rayIsClear(
        candidate.cells,
        candidate.direction,
        initialOccupancy,
        candidate.pathIndex,
        boardWidth,
        boardHeight,
      ));
    let orientationCandidates = candidates;
    if (wardenParams !== undefined) {
      const exposedWardens = candidates.filter(({ pathIndex }) =>
        wardenPathIndexes.has(pathIndex));
      if (exposedWardens.length > 0 && prefers(rng, wardenParams.selectionPreference)) {
        orientationCandidates = exposedWardens;
        const chainedWardens = exposedWardens.filter((candidate) => {
          const blocker = initialOccupancy === undefined ? undefined : firstBlockerPathIndex(
            candidate,
            initialOccupancy,
            boardWidth,
            boardHeight,
          );
          return blocker !== undefined && removedWardenPathIndexes.has(blocker);
        });
        if (chainedWardens.length > 0
          && prefers(rng, wardenParams.chainOrientationPreference)) {
          orientationCandidates = chainedWardens;
        } else {
          const constructiveWardens = exposedWardens.filter(({ tipAtPathStart }) =>
            tipAtPathStart);
          if (constructiveWardens.length > 0) orientationCandidates = constructiveWardens;
        }
      } else {
        const victims = candidates.filter((candidate) => {
          const blocker = initialOccupancy === undefined ? undefined : firstBlockerPathIndex(
            candidate,
            initialOccupancy,
            boardWidth,
            boardHeight,
          );
          return blocker !== undefined && removedLongPathIndexes.has(blocker);
        });
        if (victims.length > 0 && prefers(rng, wardenParams.victimOrientationPreference)) {
          orientationCandidates = victims;
        } else {
          const maximumCoverage = Math.max(
            ...candidates.map(({ pathIndex }) => blockerCoverage.get(pathIndex) ?? 0),
          );
          const usefulCandidates = candidates.filter(({ pathIndex }) =>
            (blockerCoverage.get(pathIndex) ?? 0) === maximumCoverage);
          const constructiveCandidates = usefulCandidates.filter(({ tipAtPathStart }) =>
            tipAtPathStart);
          orientationCandidates = constructiveCandidates.length > 0
            ? constructiveCandidates
            : usefulCandidates;
        }
      }
    }
    const selectedPathIndexes = new Set(orientationCandidates.map(({ pathIndex }) => pathIndex));
    orientationCandidates = [
      ...orientationCandidates.filter(({ pathIndex }) =>
        !authoredInterestPathIndexes.has(pathIndex)),
      ...candidates.filter(({ pathIndex }) =>
        authoredInterestPathIndexes.has(pathIndex) && selectedPathIndexes.has(pathIndex)),
    ];
    const blockedOrientationCandidates = initiallyBlocked.filter((candidate) =>
      orientationCandidates.includes(candidate)
      && !authoredInterestPathIndexes.has(candidate.pathIndex));
    const preferBlocked = blockedOrientationCandidates.length > 0
      && (blockedPreference === 1 || rng() < blockedPreference);
    let selectionPool = preferBlocked
      ? blockedOrientationCandidates
      : orientationCandidates;
    const reservedCandidates = selectionPool.filter((candidate) => {
      const reserved = initialInterestPlan?.orientations.get(candidate.pathIndex);
      return reserved !== undefined && sameOrientation(candidate, reserved);
    });
    if (reservedCandidates.length > 0) selectionPool = reservedCandidates;
    if (chainTrapParams !== undefined && initialOccupancy !== undefined) {
      const interestContext = interestSelectionContext(interestArrows, arePathsSideAdjacent);
      const scored = selectionPool.map((candidate) => {
        const initiallyRemovable = rayIsClear(
          candidate.cells,
          candidate.direction,
          initialOccupancy,
          candidate.pathIndex,
          boardWidth,
          boardHeight,
        );
        return {
          candidate,
          score: interestSelectionScore(
            candidate,
            initiallyRemovable,
            interestArrows,
            chainTrapParams,
            pathsByIndex.size,
            arePathsSideAdjacent,
            interestContext,
          ),
        };
      });
      const maximumScore = Math.max(...scored.map(({ score }) => score));
      selectionPool = scored.filter(({ score }) => score === maximumScore)
        .map(({ candidate }) => candidate);
    }
    const selected = pickOne(rng, selectionPool);
    const initialBlocker = initialOccupancy === undefined ? undefined : firstBlockerPathIndex(
      selected,
      initialOccupancy,
      boardWidth,
      boardHeight,
    );
    if (initialOccupancy !== undefined && rayIsClear(
      selected.cells,
      selected.direction,
      initialOccupancy,
      selected.pathIndex,
      boardWidth,
      boardHeight,
    )) initialRemovableCount += 1;
    const arrowIndex = arrows.length;
    arrows.push({
      id: `path-${arrows.length}`,
      cells: selected.cells,
      direction: selected.direction,
    });
    if (chainTrapParams !== undefined) {
      interestArrows.push({
        arrow: { cells: selected.cells, direction: selected.direction },
        pathIndex: selected.pathIndex,
        initiallyRemovable: initialBlocker === undefined,
      });
    }
    arrowIndexByPathIndex.set(selected.pathIndex, arrowIndex);
    if (wardenPathIndexes.has(selected.pathIndex)) {
      removedWardenPathIndexes.add(selected.pathIndex);
      wardenArrowIndexes.push(arrowIndex);
    }
    if (edgeSentinels.has(selected.pathIndex)) edgeSentinelArrowIndexes.push(arrowIndex);
    if (interestPathIndexes.has(selected.pathIndex)) interestArrowIndexes.push(arrowIndex);
    if (longPathIndexes.has(selected.pathIndex)) {
      removedLongPathIndexes.add(selected.pathIndex);
    }
    if (initialBlocker !== undefined && removedWardenPathIndexes.has(initialBlocker)) {
      const wardenArrowIndex = arrowIndexByPathIndex.get(initialBlocker);
      if (wardenArrowIndex !== undefined) {
        wardenVictimPairs.push({ wardenArrowIndex, victimArrowIndex: arrowIndex });
      }
    }
    remaining.delete(selected.pathIndex);
    edgeSentinels.delete(selected.pathIndex);
    selected.cells.forEach((cell) => {
      occupancy[cell.y * boardWidth + cell.x] = 0;
    });
  }

  const oriented = {
    arrows,
    solutionOrder: arrows.map((_, index) => index),
    initialRemovableCount: initialOccupancy === undefined ? undefined : initialRemovableCount,
    wardenArrowIndexes: wardenParams === undefined ? undefined : wardenArrowIndexes,
    edgeSentinelArrowIndexes: edgeParams === undefined ? undefined : edgeSentinelArrowIndexes,
    interestArrowIndexes: interestPathIndexes.size === 0 ? undefined : interestArrowIndexes,
    wardenVictimPairs: wardenParams === undefined ? undefined : wardenVictimPairs,
  };
  return wardenParams === undefined ? oriented : splitNonWardenLongPaths(
    arrows,
    wardenArrowIndexes,
    edgeSentinelArrowIndexes,
    interestArrowIndexes,
    wardenParams,
    boardWidth,
    boardHeight,
    rng,
  );
}
